import React, { useState, useRef } from 'react';
import styles from './O_FileUploader.module.css';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import cn from 'classnames';
import { useTranslation } from '@/hooks/useTranslation';

interface O_FileUploaderProps {
  onFilesUploaded?: (files: FileList) => void;
}

export default function O_FileUploader({
  onFilesUploaded,
}: O_FileUploaderProps) {
  const t = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const validateFile = (file: File): string | null => {
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
      return t('capsuleCreate.contentUpload.fileTooLarge')
        .replace('{{fileName}}', file.name)
        .replace('{{size}}', sizeInMB)
        .replace('{{maxSize}}', '20');
    }

    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'audio/mpeg',
      'video/mp4',
      'video/quicktime',
    ];

    if (!allowedTypes.includes(file.type)) {
      const allowedFormats = t('capsuleCreate.contentUpload.allowedFormats');
      return t('capsuleCreate.contentUpload.unsupportedFormat')
        .replace('{{fileName}}', file.name)
        .replace('{{formats}}', allowedFormats);
    }

    return null;
  };

  const handleFiles = (files: FileList) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      const errorMessage = `${t('capsuleCreate.contentUpload.uploadErrors')}\n\n${errors.join('\n\n')}`;
      alert(errorMessage);
    }

    if (validFiles.length > 0) {
      const validFileList = new DataTransfer();
      validFiles.forEach(file => validFileList.items.add(file));
      onFilesUploaded?.(validFileList.files);

      if (errors.length > 0) {
        const successMessage = t(
          'capsuleCreate.contentUpload.filesUploadedSuccess'
        ).replace('{{count}}', String(validFiles.length));
        setTimeout(() => alert(successMessage), 100);
      }
    }
  };

  return (
    <div
      className={cn(styles.wrapper, { [styles.drag_over]: isDragOver })}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type='file'
        className={styles.hidden_input}
        onChange={handleFileSelect}
        multiple
        accept='.jpg,.jpeg,.png,.gif,.webp,.mp3,.mp4,.mov'
      />
      <A_CircleButton
        icon={<Q_Icon.UploadIcon width={24} height={24} fill='none' />}
        variant='secondary'
        onClick={() => {
          fileInputRef.current?.click();
        }}
      />
      <div className={styles.text_wrapper}>
        <p className={cn(styles.title, 'text-p-4')}>
          {t('capsuleCreate.contentUpload.dragAndDrop')}
        </p>
        <p className={cn(styles.text, 'text-p-7')}>
          {t('capsuleCreate.contentUpload.fileFormats')}
        </p>
        <p className={cn(styles.text, 'text-p-7')}>
          {t('capsuleCreate.contentUpload.livePhotoNote')}
        </p>
      </div>
    </div>
  );
}
